import type { CollectionSlug } from 'payload';

import { CHANGE_REQUEST_DOCUMENT_COLLECTIONS } from '@/collections/ChangeRequests/document-collections';

/**
 * The data attribute a rendered element carries on a Preview Site or a
 * Shared Draft, naming the content it shows: a Subject (see CONTEXT.md). The
 * Change Request widget walks up from the element a Reviewer pointed at and
 * sends every value it passes, innermost first.
 */
export const SUBJECT_ATTRIBUTE = 'data-change-request-subject';

/**
 * The data attribute naming a whole-page template's section (`hero`, `faq`,
 * ...) on that section's root. It carries no document: walking up, the widget
 * gives the innermost section it passes to the next Subject above it (the
 * block row, or for a service landing the service), so a template never needs
 * its page's id threaded down to each section.
 */
export const SECTION_ATTRIBUTE = 'data-change-request-section';

/** At most this many Subjects are kept per request, the page's document included. */
export const MAX_SUBJECTS = 10;

/**
 * Every collection a Subject can name: the documents pages render, plus what
 * a page shows from elsewhere (images, reviews, portfolio items, locations,
 * forms), the per-tenant singletons that supply site-wide parts and page
 * settings, and the tenant itself (its business details). A component that
 * starts showing a new collection adds it here.
 */
export const SUBJECT_COLLECTIONS = [
  ...CHANGE_REQUEST_DOCUMENT_COLLECTIONS,
  'media',
  'reviews',
  'portfolio-items',
  'locations',
  'forms',
  'page-headers',
  'page-footers',
  'service-pages',
  'service-category-pages',
  'team-member-pages',
  'blog-index-pages',
  'portfolio-index-pages',
  'concern-index-pages',
  'tenants',
] as const satisfies readonly CollectionSlug[];

export type SubjectCollection = (typeof SUBJECT_COLLECTIONS)[number];

/**
 * One piece of content: a document, optionally narrowed to a block row on it
 * and a section of that block (a whole-page template's named part, such as
 * `hero` or `faq`). A Subject goes no deeper than the section: the pin's own
 * text and image let staff or an agent find the field.
 */
export interface SubjectRef {
  collection: SubjectCollection;
  id: string;
  block?: string;
  section?: string;
}

const ID = '[A-Za-z0-9_-]{1,64}';
const SECTION = '[a-z][a-zA-Z0-9-]{0,39}';
const SUBJECT_PATTERN = new RegExp(`^([a-z-]+):(${ID})(?:#(${ID}))?(?:@(${SECTION}))?$`);

function isSubjectCollection(value: string): value is SubjectCollection {
  return (SUBJECT_COLLECTIONS as readonly string[]).includes(value);
}

/** `<collection>:<id>[#<block row>][@<section>]`, as the attribute carries it. */
export function formatSubject({ collection, id, block, section }: SubjectRef): string {
  return `${collection}:${id}${block ? `#${block}` : ''}${section ? `@${section}` : ''}`;
}

/** Reads an attribute value the widget sent back; null for anything malformed or naming an unknown collection. */
export function parseSubject(value: string): SubjectRef | null {
  const match = SUBJECT_PATTERN.exec(value);
  if (!match) return null;
  const [, collection = '', id = '', block, section] = match;
  if (!isSubjectCollection(collection)) return null;
  return { collection, id, ...(block ? { block } : {}), ...(section ? { section } : {}) };
}

/** Two references to the same document, block and section. */
export function sameSubject(a: SubjectRef, b: SubjectRef): boolean {
  return formatSubject(a) === formatSubject(b);
}

const SECTION_PATTERN = new RegExp(`^${SECTION}$`);

/** Whether `name` can be a section name: a lowercase start, then letters, digits and hyphens. */
export function isSectionName(name: string): boolean {
  return SECTION_PATTERN.test(name);
}
